import dedent from 'ts-dedent'

export default {
  title: 'Maps',
  titleButton: 'New map',
  titleButtonShort: 'New',
  errorCurrentMapLocked: 'The map is currently being edited by another user. Try again later',
  emptyState: {
    caption: `You haven't created any maps yet`,
    button: 'Create your first map',
    canNotCreate: `No maps have been created yet`,
    noMapsBeenShared: `No maps have been shared with you yet.`,
    howToGetStarted: {
      title: 'How to get started',
      examples: {
        title: 'Map examples',
        description: 'Get inspired by our examples'
      },
      video: {
        title: 'Quickstart video',
        description: 'How to create your first map'
      },
      documentation: {
        title: 'Documentation',
        description: 'Unlock the power of the platform'
      },
      academy: {
        title: 'CARTO Academy',
        description: 'Learning paths and tutorials'
      }
    },
    demoMaps: {
      overline: 'Need ideas?',
      title: 'Try our demo maps'
    }
  },
  picker: {
    empty: 'There are no maps to pick'
  },
  filters: {
    search: 'Search map',
    title: '{elements} results by "{query}"',
    noResults: 'No maps were found',
    order: {
      updatedAt: 'Last updated'
    },
    privacy: {
      all: 'All',
      myMaps: 'My maps',
      myAIAgents: 'My AI Agents',
      shared: 'Organization',
      public: 'Public'
    },
    total: `{itemCount} {itemCount, plural,
        one {map}
        other {maps}
    }`
  },
  edit: {
    title: 'Edit map properties',
    inputs: {
      name: 'Name',
      description: 'Description',
      descriptionDefaultText: dedent(`
        ### "Your Title Here" 🌍

        ![Image: Replace with your own](https://insert-image-url-here.com)

        Welcome to your map's description panel! Below is a sample template to guide you, but feel free to express in your style. Markdown formatting is supported.

        ---

        #### About This Map:
        _Describe the main theme of your map here._

        #### Data Insights:
        _Share key findings or patterns visible in your map._

        ---
        #### Sample Markdown Tips:
        - \`##\` for headings.
        - \`**Bold**\` for **Bold**.
        - \`_Italicize_\` for _Italicize_.
        - \`[Link Text](#)\` to add links.
      `)
    },
    helpers: {
      description: 'Remaining characters: {remainingCharacters}'
    },
    buttons: {
      save: 'Save changes'
    }
  },
  cards: {
    tooltips: {
      publicMap: 'Public map',
      sharedMap: 'Shared with Organization',
      privateMap: 'Private map',
      agentEnabled: 'AI Agent created'
    },
    actions: {
      edit: 'Edit map properties',
      share: 'Share',
      delete: 'Delete',
      editThisDemo: 'Edit this demo',
      copyId: 'Copy map ID',
      moveTo: 'Move to',
      removeFromContainer: 'Remove from {container}'
    },
    deleteDialog: {
      title: 'Delete map',
      publicDescription:
        'Are you sure you want to delete {name}? This action cannot be undone and the public version of the map will become unavailable.',
      sharedDescription:
        'Are you sure you want to delete {name} from your organization? This action cannot be undone and other users will lose access to it.',
      privateDescription: 'Are you sure you want to delete {name}? This action cannot be undone.',
      errorMessage: 'Could not delete map, please try again',
      delete: 'Yes, delete'
    },
    demo: {
      tag: 'Demo'
    }
  },
  listView: {
    columns: {
      mapName: 'Map Name',
      owner: 'Owner',
      tags: 'Tags',
      lastUpdate: 'Last update'
    }
  },
  sample: {
    overline: 'Need ideas?',
    title: 'How to get started'
  },
  tutorials: {
    overline: 'Learn how',
    title: 'Related tutorials'
  },
  default: {
    title: 'Untitled'
  },
  creatingDemoMap: 'Creating demo map...',
  quota: {
    limitHit:
      'You have used all the maps ({limit}) available in your current plan. Request an upgrade to continue creating maps',
    wouldExceedQuotaModal: {
      create: {
        title: "Couldn't create new map",
        message: 'Check the exceeded quotas below before creating a new map.'
      },
      duplicate: {
        title: "Couldn't duplicate map",
        message: 'Check the exceeded quotas below before duplicating a map.'
      },
      share: {
        title: "Can't share more maps",
        message: 'Check the exceeded quotas below before sharing more maps.',
        alertMessage:
          'You can’t share any more maps with your current plan. Free a map by modifying or removing other maps, or request to be contacted for an upgrade.'
      },
      moreInfo: {
        title: "Can't create new maps"
      },

      quotaTitle: 'Maps',
      alertMessage:
        'You can’t create any more maps with your current plan. Free up space by deleting other maps or request to be contacted for an upgrade.'
    },

    shareMapsQuotaHitWarning:
      'You have used all the public maps ({limit}) available in your current plan. Request an upgrade to continue publishing maps.'
  },
  versionChanges: {
    dataSourceAdded: '{count, plural, one {# data source was added} other {# data sources were added}}',
    dataSourceRemoved: '{count, plural, one {# data source was removed} other {# data sources were removed}}',
    dataSourceUpdated: '{count, plural, one {# data source was updated} other {# data sources were updated}}',
    layerAdded: '{count, plural, one {# layer was added} other {# layers were added}}',
    layerRemoved: '{count, plural, one {# layer was removed} other {# layers were removed}}',
    layerUpdated: '{count, plural, one {# layer was updated} other {# layers were updated}}',
    groupAdded: '{count, plural, one {# layer group was added} other {# layer groups were added}}',
    groupRemoved: '{count, plural, one {# layer group was removed} other {# layer groups were removed}}',
    groupUpdated: '{count, plural, one {# layer group was updated} other {# layer groups were updated}}',
    layerInteractionUpdated:
      '{count, plural, one {# layer interaction was updated} other {# layer interactions were updated}}',
    layerLegendUpdated: '{count, plural, one {# layer legend was updated} other {# layer legends were updated}}',
    basemapUpdated: 'The basemap was updated',
    widgetAdded: '{count, plural, one {# widget was added} other {# widgets were added}}',
    widgetRemoved: '{count, plural, one {# widget was removed} other {# widgets were removed}}',
    widgetUpdated: '{count, plural, one {# widget was updated} other {# widgets were updated}}',
    sqlParameterAdded: '{count, plural, one {# parameter was added} other {# parameters were added}}',
    sqlParameterRemoved: '{count, plural, one {# parameter was removed} other {# parameters were removed}}',
    sqlParameterUpdated: '{count, plural, one {# parameter was updated} other {# parameters were updated}}',
    mapViewUpdated: 'The map view mode was updated',
    dataFreshnessUpdated: 'The data freshness was updated',
    mapSettingsUpdated: 'The map settings for viewers were updated',
    agentCreated: 'Agent was created',
    agentRemoved: 'Agent was removed',
    agentConfigUpdated: 'The agent configuration was updated',
    filtersUpdated: 'Updated filters',
    descriptionUpdated: 'The map description was updated',
    widgetsPanelLayoutUpdated: 'The widgets panel layout was updated',
    published: 'The map was published',
    noChanges: 'No new changes'
  },
  sharingDialog: {
    alertConfirmationContent:
      'The map has not yet been shared with other users. If you close the dialog now, the map will keep the current permissions.',
    restrictedTooltipLabel:
      'This map is accessible to the entire organization because access is granted through its parent folder.',
    developerSettings: {
      title: 'Sharing options',
      button: 'Sharing options',
      sectionTitle: 'Developers and embedding',
      urlParameters: {
        title: 'URL parameters'
      }
    },
    allowEditing: {
      label: 'Allow editing for editors',
      disabledTooltip: 'Share your map with others to enable this option'
    },
    collaborationAlert: {
      privateConnectionsCollaborationOff:
        'One or more connections used in this map are not shared with the other editors. To enable editing set your connections to shared.',
      privateConnectionsCollaborationOn:
        'One or more connections used in this map are not shared with the other editors, so the map may not work properly. Share the connections or disable collaboration.',
      privateConnectionsEditor:
        'One or more connections used in this map are not shared with other editors. This map may not work properly for collaboration. Please contact the owner to share the required connections to use this map.'
    },
    publicConfirmation: {
      title: 'Would you like to publish your latest changes?',
      description: 'Making this map public will publish your latest updates and make them visible to everyone.',
      confirm: 'Yes, publish'
    }
  }
}
